import { useState, useEffect } from 'react';
import ReactFlow, { Background, Controls, Handle, Position, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { Eye, EyeOff, Map, Loader2, HelpCircle, X, Info } from 'lucide-react';
import api from '../api';
import { calculateAllocations } from '../utils/routingMath';

// Custom Nodes
function IncomeNode({ data }) {
  return (
    <div className={`p-4 rounded-xl border-2 shadow-lg w-64 ${data.privacyMode ? 'bg-slate-800' : 'bg-slate-900'} border-blue-500`} style={{ background: 'var(--bg-color)', borderColor: 'var(--primary)', borderStyle: 'solid', borderWidth: '2px', borderRadius: '12px', padding: '16px', color: 'white', position: 'relative' }}>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Income Source</div>
      <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginTop: '4px' }}>{data.name}</div>
      <div style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.5rem', marginTop: '8px' }}>
        {data.privacyMode ? '$***.**' : `$${data.amount.toLocaleString(undefined, {minimumFractionDigits:2})}`}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: 'var(--primary)', width: '10px', height: '10px', right: '-6px' }} />
    </div>
  );
}

function RuleNode({ data }) {
  const isDry = data.isDry;
  return (
    <div style={{ background: isDry ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.05)', border: `1px solid ${isDry ? '#374151' : 'var(--secondary)'}`, borderRadius: '8px', padding: '12px', width: '220px', color: 'white', opacity: isDry ? 0.6 : 1, position: 'relative', backdropFilter: 'blur(10px)' }}>
      <Handle type="target" position={Position.Left} style={{ background: isDry ? '#374151' : 'var(--secondary)', width: '8px', height: '8px', left: '-5px' }} />
      <div style={{ fontSize: '0.75rem', color: isDry ? '#6b7280' : 'var(--secondary)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{data.type} RULE</div>
      <div style={{ fontWeight: 'bold', fontSize: '1rem', marginTop: '4px' }}>{data.name}</div>
      <div style={{ fontSize: '0.85rem', marginTop: '4px', fontFamily: 'monospace', color: isDry ? '#6b7280' : 'var(--primary)' }}>
        {data.privacyMode ? '***' : data.amountText}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: isDry ? '#374151' : 'var(--secondary)', width: '8px', height: '8px', right: '-5px' }} />
    </div>
  );
}

function BucketNode({ data }) {
  const isDry = data.isDry;
  return (
    <div style={{ background: isDry ? 'rgba(0,0,0,0.5)' : 'var(--bg-color)', border: `2px solid ${isDry ? '#374151' : 'var(--success)'}`, borderRadius: '12px', padding: '16px', width: '250px', color: 'white', opacity: isDry ? 0.6 : 1, position: 'relative' }}>
      <Handle type="target" position={Position.Left} style={{ background: isDry ? '#374151' : 'var(--success)', width: '10px', height: '10px', left: '-6px' }} />
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Target Fund</div>
      <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginTop: '4px' }}>{data.name}</div>
      <div style={{ marginTop: '8px', color: 'var(--success)', fontFamily: 'monospace', fontSize: '1rem', background: 'rgba(16, 185, 129, 0.1)', display: 'inline-block', padding: '2px 8px', borderRadius: '4px' }}>
        + {data.privacyMode ? '$***.**' : `$${data.fundedAmount.toLocaleString(undefined, {minimumFractionDigits:2})}`}
      </div>
      {isDry && <div style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'var(--danger)', color: 'white', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '8px', fontWeight: 'bold' }}>DRY</div>}
    </div>
  );
}

const nodeTypes = {
  incomeNode: IncomeNode,
  ruleNode: RuleNode,
  bucketNode: BucketNode
};

export default function MoneyMap() {
  const [incomes, setIncomes] = useState([]);
  const [selectedIncomeId, setSelectedIncomeId] = useState('');
  const [funds, setFunds] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [privacyMode, setPrivacyMode] = useState(false);
  const [simulatorAmount, setSimulatorAmount] = useState(0);
  const [isSimulationMode, setIsSimulationMode] = useState(false);
  const [insights, setInsights] = useState([]);
  const [showInstructions, setShowInstructions] = useState(() => {
    const saved = localStorage.getItem('money_map_guide_visible');
    return saved !== 'false';
  });
  
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [fundsRes, incomesRes, allocRes] = await Promise.all([
        api.get('/funds'),
        api.get('/incomes'),
        api.get('/allocations')
      ]);
      setFunds(fundsRes.data);
      setIncomes(incomesRes.data);
      setAllocations(allocRes.data);
      if (incomesRes.data.length > 0) {
        setSelectedIncomeId(incomesRes.data[0].id);
        setSimulatorAmount(incomesRes.data[0].amount);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedIncomeId) {
       buildGraph();
     }
  }, [selectedIncomeId, incomes, funds, allocations, simulatorAmount, privacyMode, isSimulationMode]);

  const toggleInstructions = (visible) => {
    setShowInstructions(visible);
    localStorage.setItem('money_map_guide_visible', String(visible));
  };

  const handleIncomeSelect = (id) => {
    setSelectedIncomeId(id);
    const inc = incomes.find(i => i.id === id);
    if (inc) {
      setSimulatorAmount(inc.amount);
      setIsSimulationMode(false);
    }
  };

  const buildGraph = () => {
    const inc = incomes.find(i => i.id === selectedIncomeId);
    if (!inc) return;

    const allocs = allocations.filter(a => a.income_id === inc.id).sort((a,b) => a.priority - b.priority);

    const depositToUse = isSimulationMode ? parseFloat(simulatorAmount) || 0 : inc.amount;
    const { fundUpdates, dryNodes, currentRemaining, ruleResults } = calculateAllocations(depositToUse, inc.frequency, allocs);

    const newNodes = [];
    const newEdges = [];

    // Col 1: Income
    newNodes.push({
      id: `income-${inc.id}`,
      type: 'incomeNode',
      position: { x: 50, y: Math.max(0, (allocs.length * 140) / 2) },
      data: { name: inc.nickname || inc.name, amount: depositToUse, privacyMode }
    });

    // Generate Rules and Buckets
    allocs.forEach((a, index) => {
      const fund = funds.find(f => f.id === a.fund_id);
      if (!fund) return;
      const isDry = dryNodes.includes(a.fund_id) || (fundUpdates[a.fund_id] || 0) === 0;
      
      let amountText = '';
      if (a.type === 'fixed') amountText = `$${parseFloat(a.amount).toFixed(2)}`;
      if (a.type === 'percentage') amountText = `${a.amount}%`;
      if (a.type === 'remainder') amountText = 'Remainder';

      // Rule Node (Col 2)
      const ruleId = `rule-${a.id}`;
      newNodes.push({
        id: ruleId,
        type: 'ruleNode',
        position: { x: 450, y: index * 140 + 50 },
        data: { name: fund.name, type: a.type, amountText, isDry, privacyMode }
      });

      // Edge from Income to Rule
      newEdges.push({
        id: `e-inc-rule-${a.id}`,
        source: `income-${inc.id}`,
        target: ruleId,
        animated: !isDry && isSimulationMode,
        style: { stroke: isDry ? '#374151' : 'var(--primary)', strokeWidth: 2 },
      });

      // Bucket Node (Col 3)
      const bucketId = `bucket-${fund.id}`;
      if (!newNodes.find(n => n.id === bucketId)) {
        const bucketRules = allocs.filter(ar => ar.fund_id === fund.id);
        const avgIndex = allocs.findIndex(ar => ar.id === bucketRules[0].id);
        
        newNodes.push({
          id: bucketId,
          type: 'bucketNode',
          position: { x: 850, y: avgIndex * 140 + 40 },
          data: { name: fund.name, fundedAmount: fundUpdates[fund.id] || 0, isDry, privacyMode }
        });
      }

      // Edge from Rule to Bucket
      newEdges.push({
        id: `e-rule-bucket-${a.id}`,
        source: ruleId,
        target: bucketId,
        animated: !isDry && isSimulationMode,
        style: { stroke: isDry ? '#374151' : 'var(--success)', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: isDry ? '#374151' : 'var(--success)' }
      });
    });

    setNodes(newNodes);
    setEdges(newEdges);

    if (isSimulationMode && ruleResults) {
      const newInsights = [];
      const starvedRules = ruleResults.filter(r => r.status === 'STARVED');
      const cappedRules = ruleResults.filter(r => r.status === 'CAPPED');
      
      if (starvedRules.length > 0) {
        const totalMissing = starvedRules.reduce((sum, r) => sum + (r.expectedAmount - r.actualAmount), 0);
        newInsights.push({
          type: 'starved',
          color: 'var(--danger)',
          text: `Alert: ${starvedRules.length} bucket(s) were underfunded due to a paycheck shortfall (Total missing: $${totalMissing.toLocaleString(undefined, {minimumFractionDigits: 2})}).`
        });
      }
      
      if (cappedRules.length > 0) {
        newInsights.push({
          type: 'capped',
          color: 'var(--success)',
          text: `Goal Reached: ${cappedRules.length} bucket(s) successfully reached their target cap.`
        });
      }
      
      if (currentRemaining > 0) {
        const hasRemainderRule = allocs.some(a => a.type === 'remainder');
        if (hasRemainderRule) {
           newInsights.push({
             type: 'surplus',
             color: 'var(--warning)',
             text: `Surplus Alert: Your remainder rule capped out. Consider adding a rollover fund or raising caps to capture the remaining $${currentRemaining.toLocaleString(undefined, {minimumFractionDigits: 2})}.`
           });
        } else {
           newInsights.push({
             type: 'surplus',
             color: 'var(--warning)',
             text: `Surplus Alert: You have an unallocated remainder of $${currentRemaining.toLocaleString(undefined, {minimumFractionDigits: 2})}. Consider creating a Remainder Rule.`
           });
        }
      }
      
      setInsights(newInsights);
    } else {
      setInsights([]);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 80px)', background: isSimulationMode ? 'rgba(245, 158, 11, 0.03)' : 'transparent', borderRadius: '16px', overflow: 'hidden', border: isSimulationMode ? '1px solid rgba(245, 158, 11, 0.2)' : 'none', position: 'relative' }}>
      
      {/* Header */}
      <div className="glass mobile-flex-col" style={{ margin: '16px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10, gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ background: 'rgba(59, 130, 246, 0.2)', padding: '8px', borderRadius: '8px', color: 'var(--primary)' }}>
            <Map size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Visual Money Map</h2>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>See exactly how your paycheck flows</p>
          </div>
        </div>

        <div className="mobile-flex-col" style={{ display: 'flex', alignItems: 'center', gap: '24px', width: '100%', justifyContent: 'flex-end' }}>
          {incomes.length > 0 ? (
            <select 
              value={selectedIncomeId} 
              onChange={(e) => handleIncomeSelect(e.target.value)}
              style={{ minWidth: '180px', padding: '8px 12px', borderRadius: '8px' }}
            >
              {incomes.map(i => (
                <option key={i.id} value={i.id}>{i.nickname || i.name}</option>
              ))}
            </select>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>No Incomes Setup</span>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '24px', borderLeft: '1px solid var(--surface-border)' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Simulate: $</span>
            <input 
              type="number" 
              value={simulatorAmount}
              onChange={(e) => {
                setSimulatorAmount(e.target.value);
                setIsSimulationMode(true);
              }}
              style={{ width: '120px', padding: '8px', borderRadius: '8px', border: isSimulationMode ? '1px solid var(--warning)' : '1px solid var(--surface-border)', color: isSimulationMode ? 'var(--warning)' : 'white' }}
            />
            {isSimulationMode && (
              <span style={{ fontSize: '0.7rem', background: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(245, 158, 11, 0.3)', fontWeight: 'bold' }}>
                SIMULATION
              </span>
            )}
          </div>

          <button 
            onClick={() => setPrivacyMode(!privacyMode)}
            className="btn btn-glass"
            style={{ padding: '8px', background: privacyMode ? 'var(--primary)' : 'transparent', color: privacyMode ? 'white' : 'var(--text-muted)' }}
            title="Toggle Privacy Mode"
          >
            {privacyMode ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      {/* Collapsible Guide Panel */}
      {showInstructions ? (
        <div className="glass" style={{ margin: '0 16px 16px 16px', padding: '16px', position: 'relative', border: '1px solid rgba(59, 130, 246, 0.3)', background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(12px)', borderRadius: '12px', zIndex: 10 }}>
          <button 
            onClick={() => toggleInstructions(false)}
            style={{ position: 'absolute', top: '12px', right: '12px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
            title="Dismiss Guide"
          >
            <X size={18} />
          </button>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <Info className="text-primary" size={20} style={{ marginTop: '2px', flexShrink: 0 }} />
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', fontWeight: 'bold', color: 'white' }}>How to use your Money Map</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                This map visualizes the automatic flow of your income. Adjust rules, run dry-run simulations, and inspect allocations visually.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', fontSize: '0.8rem' }}>
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <strong style={{ color: 'var(--primary)', display: 'block', marginBottom: '4px' }}>1. Flow Columns</strong>
                  Money flows left-to-right from your <span style={{ color: 'var(--primary)' }}>Income Source</span>, through configured <span style={{ color: 'var(--secondary)' }}>Rules</span> (evaluated by priority), into final target <span style={{ color: 'var(--success)' }}>Funds</span>.
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <strong style={{ color: 'var(--warning)', display: 'block', marginBottom: '4px' }}>2. Interactive Simulator</strong>
                  Type any amount into the <strong>Simulate</strong> input to run a visual dry-run. A gold tint & badge indicates you are in safe Simulation mode.
                </div>
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <strong style={{ color: '#94a3b8', display: 'block', marginBottom: '4px' }}>3. Dry Nodes (Grayed Out)</strong>
                  Nodes marked as <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>DRY</span> represent rules or funds that received $0.00 because higher priority rules fully consumed the paycheck.
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '0 16px 8px 16px' }}>
          <button 
            onClick={() => toggleInstructions(true)}
            className="btn btn-glass"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', padding: '4px 10px', borderRadius: '20px' }}
          >
            <HelpCircle size={14} /> Show Map Guide
          </button>
        </div>
      )}

      {/* Map Canvas */}
      <div style={{ flex: 1, position: 'relative', width: '100%', height: '100%', minHeight: '500px' }}>
        {/* Hovering Insights Panel */}
        {isSimulationMode && insights.length > 0 && (
          <div className="glass animate-fade-in" style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 10, maxWidth: '400px', background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={16} className="text-warning" /> Simulation Insights
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', paddingRight: '4px' }}>
              {insights.map((insight, idx) => (
                <div key={idx} style={{ fontSize: '0.8rem', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: `3px solid ${insight.color}` }}>
                   {insight.text}
                </div>
              ))}
            </div>
          </div>
        )}

        {allocations.filter(a => a.income_id === selectedIncomeId).length === 0 ? (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'var(--text-muted)' }}>
            <h3>No rules to visualize</h3>
            <p>Go to Paycheck Rules to add rules for this income source.</p>
          </div>
        ) : (
          <ReactFlow 
            nodes={nodes} 
            edges={edges} 
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-right"
          >
            <Background color="#334155" gap={24} />
            <Controls />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
